//#region src/pwa/PwaUpdateService.ts
var DEFAULT_OPTIONS = {
	checkIntervalMinutes: 60,
	autoApplyOnStart: true
};
var PwaUpdateServiceImpl = class {
	constructor() {
		this._listeners = /* @__PURE__ */ new Set();
		this._options = { ...DEFAULT_OPTIONS };
		this._active = false;
		this._applying = false;
		this._reloading = false;
		this._hadController = false;
		this._handleUpdateFound = () => {
			const installing = this._registration?.installing;
			if (!installing) return;
			installing.addEventListener("statechange", () => {
				if (installing.state === "installed" && navigator.serviceWorker.controller) {
					this._waitingWorker = this._registration?.waiting ?? installing;
					this._notifyListeners();
				}
			});
		};
		this._handleControllerChange = () => {
			if (!this._hadController) {
				this._hadController = true;
				return;
			}
			if (this._reloading) return;
			this._reloading = true;
			window.location.reload();
		};
		this._handleVisibilityChange = () => {
			if (document.visibilityState === "visible") this.checkForUpdate();
		};
	}
	/**
	* Start watching the service-worker registration. Idempotent and SSR-safe;
	* a no-op when the browser has no service-worker support or the app ships
	* none. Called from `startInit` with defaults — an app that wants custom
	* options calls `activate(options)` **before** `startInit()` (the first
	* call wins, later calls are ignored).
	*/
	activate(options) {
		if (this._active) return;
		if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
		this._active = true;
		this._options = {
			checkIntervalMinutes: options?.checkIntervalMinutes ?? DEFAULT_OPTIONS.checkIntervalMinutes,
			autoApplyOnStart: options?.autoApplyOnStart ?? DEFAULT_OPTIONS.autoApplyOnStart
		};
		this._hadController = Boolean(navigator.serviceWorker.controller);
		navigator.serviceWorker.addEventListener("controllerchange", this._handleControllerChange);
		document.addEventListener("visibilitychange", this._handleVisibilityChange);
		if (this._options.checkIntervalMinutes > 0) window.setInterval(() => void this.checkForUpdate(), this._options.checkIntervalMinutes * 6e4);
		this._attach();
	}
	/** True when a new version is installed and waiting to take over. */
	get updateAvailable() {
		return this._waitingWorker !== void 0;
	}
	/** True between `applyUpdate()` and the reload it triggers. */
	get applying() {
		return this._applying;
	}
	/**
	* Ask the browser to check for a new service worker now. Cheap and safe to
	* call at any time; failures (offline, transient network) are swallowed —
	* the next trigger simply tries again.
	*/
	async checkForUpdate() {
		try {
			await this._registration?.update();
		} catch {}
	}
	/**
	* Activate the waiting worker and reload once it has taken over. Returns
	* `false` when there is nothing to apply (or an apply is already running).
	*
	* The reload happens on `controllerchange`, not here — so it also fires
	* exactly once when another tab of the same app applies the update.
	*/
	applyUpdate() {
		const waiting = this._waitingWorker;
		if (!waiting || this._applying) return false;
		this._applying = true;
		this._notifyListeners();
		waiting.postMessage({ type: "SKIP_WAITING" });
		return true;
	}
	/**
	* Subscribe to update-state changes (update found, apply started).
	* @returns Unsubscribe function.
	*/
	subscribe(callback) {
		this._listeners.add(callback);
		return () => this._listeners.delete(callback);
	}
	async _attach() {
		const registration = await navigator.serviceWorker.ready;
		this._registration = registration;
		registration.addEventListener("updatefound", this._handleUpdateFound);
		if (registration.waiting && navigator.serviceWorker.controller) {
			this._waitingWorker = registration.waiting;
			if (this._options.autoApplyOnStart) {
				this.applyUpdate();
				return;
			}
			this._notifyListeners();
		}
	}
	_notifyListeners() {
		this._listeners.forEach((cb) => cb());
	}
};
/** Singleton instance of the PWA update service. */
var pwaUpdateService = new PwaUpdateServiceImpl();
//#endregion
export { pwaUpdateService };
